// News.jsx - Página de noticias para FeelLab
import React, { useState } from 'react';
import { 
  FaNewspaper, 
  FaCalendarAlt, 
  FaUser, 
  FaTag, 
  FaBookOpen, 
  FaExternalLinkAlt,
  FaHeart,
  FaPaw,
  FaLeaf,
  FaGraduationCap,
  FaSearch,
  FaFilter,
  FaPlus,
  FaClock
} from 'react-icons/fa';

const News = () => {
  const [selectedCategory, setSelectedCategory] = useState('todas');
  const [searchTerm, setSearchTerm] = useState('');

  // Noticias principales del laboratorio
  const mainNews = [
    {
      id: 1,
      title: "Titulo 1",
      excerpt: ".",
      category: "programa",
      date: "2025-01-15",
      author: "Equipo FeelLab",
      image: "/images/news/programa-escuelas.jpg",
      featured: true,
      readTime: "5 min"
    },
    {
      id: 2,
      title: "Titulo 1",
      excerpt: ".",
      category: "investigacion",
      date: "2025-01-10",
      author: "Equipo FeelLab",
      image: "/images/news/zonas-virtuales.jpg",
      featured: true,
      readTime: "7 min"
    }
  ];

  // Lecturas útiles de investigación
  const usefulReadings = [
    {
      id: 1,
      title: "Educación para el Bienestar Animal (EBA): estrategias pedagógicas para promover empatía por los animales",
      authors: ["Enrique Chaux", "Leydy Escobar", "Sergio Almeida", "Sara Quiceno"],
      source: "ResearchGate",
      url: "https://www.researchgate.net/publication/376576753_Educacion_para_el_Bienestar_Animal_EBA_estrategias_pedagogicas_para_promover_empatia_por_los_animales",
      description: "Estrategias pedagógicas innovadoras para desarrollar empatía hacia los animales en entornos educativos.",
      type: "PDF",
      category: "pedagogia"
    },
    {
      id: 2,
      title: "Creating Compassion: Using Art for Empathy Learning with Urban Youth",
      authors: ["Laura J. Morizio", "Amy L. Cook", "Rebecca Troeger", "Anna Whitehouse"],
      source: "Contemporary School Psychology - SpringerLink",
      url: "https://link.springer.com/article/10.1007/s40688-020-00346-1",
      description: "Investigación sobre el uso del arte como herramienta para desarrollar empatía en jóvenes urbanos.",
      type: "Artículo",
      category: "arte"
    },
    {
      id: 3,
      title: "Enseñanza-aprendizaje del bienestar animal: mejores estrategias en Latinoamérica",
      authors: ["World Animal Protection", "Fischer", "Pereira Bengoa", "Gebara", "Gamboa"],
      source: "ResearchGate",
      url: "https://www.researchgate.net/publication/301498164_Mejores_estrategias_practicas_y_actividades_docentes_para_la_ensenanza_efectiva_del_bienestar_animal_en_Latinoamerica",
      description: "Estudio completo sobre las mejores prácticas educativas en bienestar animal para América Latina.",
      type: "PDF",
      category: "investigacion"
    },
    {
      id: 4,
      title: "Política Nacional de Protección y Bienestar Animal",
      authors: ["Ministerio de Ambiente de Colombia"],
      source: "Ministerio de Ambiente",
      url: "https://www.minambiente.gov.co/wp-content/uploads/2025/05/VF_PoliticaNacionalProteccionBienestarAnimal_7_5_25.pdf",
      description: "Marco legal y político oficial para la protección y bienestar animal en Colombia.",
      type: "Documento oficial",
      category: "politica"
    },
    {
      id: 5,
      title: "Propuesta pedagógica en protección y bienestar animal",
      authors: ["Secretaría de Educación de Bucaramanga (SEB)"],
      source: "Secretaría de Educación de Bogotá",
      url: "https://www.seb.gov.co/wp-content/uploads/2022/06/DT-PROTECCI%C3%93N-ANIMAL-2022.pdf",
      description: "Documento con propuestas pedagógicas para implementar educación en protección animal en colegios.",
      type: "PDF",
      category: "educacion"
    },
    {
      id: 6,
      title: "Implementación de una estrategia educativa: tenencia responsable de mascotas",
      authors: ["YS Enciso Sierra"],
      source: "Repositorio Libertadores",
      url: "https://repository.libertadores.edu.co/bitstreams/fe4cfc74-7dfd-4d2d-87f7-a8ff7ebf855c/download",
      description: "Trabajo práctico sobre estrategias educativas para promover la tenencia responsable de mascotas.",
      type: "Tesis",
      category: "practica"
    }
  ];

  const categories = [
    { id: 'todas', name: 'Todas', icon: FaNewspaper, color: 'from-sky-400 to-emerald-400' },
    { id: 'programa', name: 'Programas', icon: FaGraduationCap, color: 'from-emerald-400 to-teal-400' },
    { id: 'investigacion', name: 'Investigación', icon: FaBookOpen, color: 'from-teal-400 to-emerald-400' },
    { id: 'educacion', name: 'Educación', icon: FaGraduationCap, color: 'from-amber-400 to-orange-400' },
    { id: 'alianza', name: 'Alianzas', icon: FaHeart, color: 'from-pink-400 to-rose-400' }
  ];

  const readingCategories = [
    { id: 'pedagogia', name: 'Pedagogía', color: 'emerald' },
    { id: 'arte', name: 'Arte', color: 'purple' },
    { id: 'investigacion', name: 'Investigación', color: 'blue' },
    { id: 'politica', name: 'Política', color: 'red' },
    { id: 'educacion', name: 'Educación', color: 'yellow' },
    { id: 'practica', name: 'Práctica', color: 'green' }
  ];

  // Filtrar noticias
  const filteredNews = mainNews.filter(news => {
    const matchesCategory = selectedCategory === 'todas' || news.category === selectedCategory;
    const matchesSearch = news.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         news.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryColor = (category) => {
    const colors = {
      pedagogia: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      arte: 'bg-purple-100 text-purple-700 border-purple-200',
      investigacion: 'bg-blue-100 text-blue-700 border-blue-200',
      politica: 'bg-red-100 text-red-700 border-red-200',
      educacion: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      practica: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 py-16 relative overflow-hidden">
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 text-6xl text-emerald-500 animate-bounce">📰</div>
        <div className="absolute top-40 right-20 text-4xl text-teal-500 animate-pulse">📚</div>
        <div className="absolute bottom-32 left-20 text-5xl text-sky-500 animate-bounce delay-300">🔬</div>
        <div className="absolute bottom-20 right-32 text-3xl text-emerald-600 animate-pulse delay-700">✨</div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-3 bg-white/80 rounded-full px-8 py-4 backdrop-blur-sm mb-6 shadow-lg border border-emerald-100">
            <FaPaw className="text-emerald-600 text-xl" />
            <span className="font-medium text-emerald-800">Centro de Noticias</span>
            <FaPaw className="text-emerald-600 text-xl" />
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-emerald-900 mb-4 leading-tight">
            Noticias
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              {' '}FeelLab
            </span>
          </h1>
          
          <p className="text-lg text-emerald-700 max-w-3xl mx-auto leading-relaxed">
            Mantente al día con las últimas novedades, investigaciones y avances 
            en educación para la empatía animal y bienestar.
          </p>
        </div>

        {/* Barra de búsqueda y filtros */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-emerald-100">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Búsqueda */}
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-500" />
              <input
                type="text"
                placeholder="Buscar noticias..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white border-2 border-emerald-200 rounded-xl focus:border-emerald-500 focus:ring-0 focus:outline-none transition-colors"
              />
            </div>

            {/* Botón agregar noticia */}
            <button className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2">
              <FaPlus />
              <span>Agregar Noticia</span>
            </button>
          </div>
        </div>

        {/* Filtros de categorías */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => {
            const IconComponent = category.icon;
            const isActive = selectedCategory === category.id;
            const newsCount = category.id === 'todas' ? 
              mainNews.length : 
              mainNews.filter(news => news.category === category.id).length;
            
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`group flex items-center space-x-3 px-6 py-3 rounded-full font-medium transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl ${
                  isActive
                    ? `bg-gradient-to-r ${category.color} text-white shadow-lg border-2 border-white/30`
                    : 'bg-white/80 text-emerald-700 hover:bg-white/90 border-2 border-emerald-100/50 backdrop-blur-sm'
                }`}
              >
                <IconComponent className={`text-lg ${isActive ? 'text-white' : 'text-emerald-600'}`} />
                <span>{category.name}</span>
                <span className={`text-sm px-2 py-1 rounded-full font-bold ${
                  isActive ? 'bg-white/30 text-white' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {newsCount}
                </span>
              </button>
            );
          })}
        </div>

        {/* Grid de noticias principales */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-emerald-800 mb-8 text-center">
            Últimas Noticias
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.map((news) => (
              <article
                key={news.id}
                className="group bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transform hover:-translate-y-3 transition-all duration-500 overflow-hidden border border-emerald-100/50 hover:border-emerald-200"
              >
                {/* Imagen placeholder */}
                <div className="bg-gradient-to-br from-emerald-100 to-teal-100 h-48 flex items-center justify-center relative overflow-hidden group-hover:scale-110 transition-transform duration-500">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="text-center relative z-10">
                    <FaNewspaper className="text-emerald-500 text-4xl mb-2 mx-auto" />
                    <p className="text-emerald-700 text-sm">Imagen de la noticia</p>
                  </div>
                  {news.featured && (
                    <div className="absolute top-3 left-3 bg-amber-400 text-white px-3 py-1 rounded-full text-xs font-bold">
                      Destacada
                    </div>
                  )}
                </div>

                {/* Contenido */}
                <div className="p-6">
                  {/* Metadatos */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3 text-sm text-emerald-600">
                      <FaCalendarAlt />
                      <span>{formatDate(news.date)}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-emerald-600">
                      <FaClock />
                      <span>{news.readTime}</span>
                    </div>
                  </div>

                  {/* Título */}
                  <h3 className="font-bold text-xl text-emerald-900 group-hover:text-teal-700 transition-colors mb-3 line-clamp-2">
                    {news.title}
                  </h3>

                  {/* Extracto */}
                  <p className="text-emerald-700 text-sm mb-4 leading-relaxed line-clamp-3">
                    {news.excerpt}
                  </p>

                  {/* Autor y categoría */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-xs text-emerald-600">
                      <FaUser />
                      <span>{news.author}</span>
                    </div>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-medium border border-emerald-200 capitalize">
                      {news.category}
                    </span>
                  </div>

                  {/* Botón leer más */}
                  <button className="w-full mt-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 rounded-full font-bold text-sm hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2">
                    <FaBookOpen />
                    <span>Leer más</span>
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Sección de lecturas útiles */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-emerald-100">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-emerald-800 mb-4 flex items-center justify-center space-x-3">
              <FaBookOpen className="text-emerald-600" />
              <span>Lecturas Útiles de Investigación</span>
            </h2>
            <p className="text-emerald-700 max-w-2xl mx-auto">
              Recursos académicos y documentos de referencia para profundizar en 
              educación para el bienestar animal y desarrollo de empatía.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {usefulReadings.map((reading) => (
              <div
                key={reading.id}
                className="bg-white/90 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-emerald-100 hover:border-emerald-200"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs px-3 py-1 rounded-full font-medium border ${getCategoryColor(reading.category)}`}>
                      {readingCategories.find(cat => cat.id === reading.category)?.name || reading.category}
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      {reading.type}
                    </span>
                  </div>
                </div>

                {/* Título */}
                <h3 className="font-bold text-lg text-emerald-900 mb-3 leading-tight">
                  {reading.title}
                </h3>

                {/* Autores */}
                <div className="flex items-center space-x-2 mb-3 text-sm text-emerald-600">
                  <FaUser />
                  <span>{reading.authors.join(', ')}</span>
                </div>

                {/* Descripción */}
                <p className="text-emerald-700 text-sm mb-4 leading-relaxed">
                  {reading.description}
                </p>

                {/* Fuente y enlace */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-emerald-600 font-medium">
                    Fuente: {reading.source}
                  </span>
                  <a
                    href={reading.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-emerald-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-emerald-600 transform hover:scale-105 transition-all duration-300"
                  >
                    <span>Acceder</span>
                    <FaExternalLinkAlt />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl p-8 text-white shadow-xl">
            <h3 className="text-2xl font-bold mb-4">¿Tienes una noticia que compartir?</h3>
            <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
              Comparte tus experiencias, investigaciones o iniciativas relacionadas 
              con la educación en empatía animal.
            </p>
            <button className="bg-white text-emerald-600 px-8 py-3 rounded-full font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              Enviar Noticia
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default News;